/**
 * INSULATION SCHEDULE round (2026-10-03, Matt): "it would be great if the insulation schedule was
 * derived/cross checked with what the model handles. Showing too much insulation in an assembly
 * that can't support it would be a coordination problem." The firm DRIVES the schedule (the
 * Title 24 is made to match it; when the energy consultant needs an upgrade, the firm updates the
 * spec), so the spec lives where the firm curates assemblies — on the TYPE (wall / roof / floor /
 * ceiling), as up to two insulation layers (type + R-value). The schedule on the General Notes
 * sheet (G002) is then DERIVED from the types the model actually uses, and each row is checked
 * against the type's own framing depth (the core declarations — wallCore/roofCore/floorCore/
 * ceilingCore — which already give the actual cavity depth).
 *
 * The fit check is deliberately conservative: it uses the HIGH end of each material's R per inch,
 * so it only flags an assembly that cannot physically hold the specified R (an R-19 batt in a 2×4
 * cavity), never a borderline one. Continuous rigid board and loose fill blown over ceiling joists
 * sit outside the cavity and are not depth-checked.
 *
 * Pure data + pure functions (the wallCore.ts convention).
 */
import type { ParamValue } from './types'
import { INSULATION_KINDS, insulationLayers, type InsulationLayer } from './insulationSpec'
import { listLevels, listPhases, resolveAllElements, type ModelDoc } from './doc'
import { phaseStatus } from './phasing'
import { wallCoreLabel, wallCoreSize } from './wallCore'
import { floorCoreLabel, floorCoreThicknessFt } from './floorCore'
import { roofCoreLabel, roofCoreThicknessFt } from './roofCore'
import { ceilingCoreLabel, ceilingCoreThicknessFt } from './ceilingCore'

const KIND = Object.fromEntries(INSULATION_KINDS.map((k) => [k.value, k]))

export interface InsulationFit {
  /** inches of cavity the in-cavity layers need, at the materials' best R per inch */
  neededIn: number
  /** the framing's actual depth (inches), or null when the type has no framed cavity */
  cavityIn: number | null
  fits: boolean
}

/** Does the specified insulation physically fit the type's framing? */
export function insulationFit(layers: InsulationLayer[], cavityIn: number | null, category: string): InsulationFit {
  let neededIn = 0
  for (const l of layers) {
    const rpi = KIND[l.kind]?.rPerInMax
    if (rpi == null || l.r == null) continue
    if (category === 'ceiling' && l.kind === 'blown') continue   // blown fill mounds over the joists
    neededIn += l.r / rpi
  }
  if (cavityIn == null || cavityIn <= 0) return { neededIn, cavityIn: cavityIn ?? null, fits: neededIn === 0 }
  return { neededIn, cavityIn, fits: neededIn <= cavityIn + 0.05 }
}

export type InsulationComponent = 'roof' | 'wall' | 'floor' | 'ceiling'

export interface InsulationScheduleRow {
  component: InsulationComponent
  typeId: string
  typeName: string
  /** '2×6', '11-7/8″ TJI', 'Concrete' … */
  framing: string
  layers: InsulationLayer[]
  fit: InsulationFit
  /** true when the type specifies no insulation at all (the sheet asks for it on screen) */
  unspecified: boolean
}

function framingOf(category: string, p: Record<string, ParamValue>): { label: string; depthIn: number | null } {
  if (category === 'wall') {
    const s = wallCoreSize(p)
    const core = String(p.coreType ?? 'wood')
    return { label: wallCoreLabel(p) || core, depthIn: s && (core === 'wood' || core === 'metal') ? s.actualIn : null }
  }
  if (category === 'roof') {
    return { label: roofCoreLabel(p) || 'Roof', depthIn: roofCoreThicknessFt(p) * 12 }
  }
  if (category === 'floor') {
    const t = floorCoreThicknessFt(p)
    const core = String(p.coreType ?? 'wood')
    return { label: floorCoreLabel(p) || (core === 'concrete' ? 'Slab-on-Grade' : 'Floor'), depthIn: core === 'concrete' ? null : (t == null ? null : t * 12) }
  }
  const t = ceilingCoreThicknessFt(p)
  return { label: ceilingCoreLabel(p) || 'Ceiling', depthIn: t == null ? null : t * 12 }
}

/**
 * One row per wall / roof / floor / ceiling TYPE the model uses (as-built at the last phase,
 * demolished work excluded). Walls: exterior types only (interior walls are not part of the
 * thermal envelope). Ceilings: only types that specify insulation (a finish-only ceiling under an
 * insulated roof is not an envelope component). Floors: the lowest level's, plus any floor type
 * that specifies insulation (an upper floor over an open porch or garage).
 */
export function insulationScheduleRows(model: ModelDoc): InsulationScheduleRow[] {
  const phases = listPhases(model)
  const last = phases.length ? phases[phases.length - 1].id : null
  const seen = new Map<string, InsulationScheduleRow>()
  // the lowest level by elevation — its floors sit over crawl space or on grade (envelope);
  // floors on upper levels separate two conditioned stories and are not part of the envelope
  const levels = listLevels(model)
  const lowest = levels.length ? levels.reduce((a, b) => (b.elevationFt < a.elevationFt ? b : a)).id : null
  for (const el of resolveAllElements(model)) {
    const cat = el.category
    if (cat !== 'wall' && cat !== 'roof' && cat !== 'floor' && cat !== 'ceiling') continue
    if (last) {
      const status = phaseStatus(phases, el, last)
      if (status === 'demo' || status === 'future') continue
    }
    const p = el.typeParams || {}
    if (cat === 'wall' && p.function === 'interior') continue
    const layers = insulationLayers(p)
    if (cat === 'ceiling' && !layers.length) continue
    if (cat === 'floor' && !layers.length && lowest && (el as { levelId?: string }).levelId !== lowest) continue
    const key = `${cat}|${el.typeId}`
    if (seen.has(key)) continue
    const fr = framingOf(cat, p)
    seen.set(key, {
      component: cat,
      typeId: el.typeId,
      typeName: el.type?.name || '',
      framing: fr.label,
      layers,
      fit: insulationFit(layers, fr.depthIn, cat),
      unspecified: !layers.length,
    })
  }
  const ORDER: Record<InsulationComponent, number> = { roof: 0, wall: 1, floor: 2, ceiling: 3 }
  return [...seen.values()].sort((a, b) => ORDER[a.component] - ORDER[b.component] || a.framing.localeCompare(b.framing, undefined, { numeric: true }))
}
